export type CustomerValueScaleUnit = "currency" | "percent" | "absolute" | "hours" | "count" | "ratio" | "custom";
export type CustomerValueScaleDirection = "higher_better" | "lower_better" | "target_range_better";
export type NumericScaleCompletenessMode = "bounded" | "full_coverage";

export type NumericScaleErrorCode =
  | "scale_empty"
  | "duplicate_interval_id"
  | "invalid_interval_bounds"
  | "interval_overlap"
  | "interval_gap"
  | "ambiguous_boundary"
  | "open_bound_not_outermost"
  | "score_out_of_range"
  | "non_monotonic_score"
  | "raw_value_unmatched"
  | "raw_value_ambiguous";

export type QualitativeScaleErrorCode =
  | "qualitative_scale_too_small"
  | "duplicate_level_id"
  | "duplicate_level_order"
  | "empty_level_label"
  | "qualitative_score_out_of_range"
  | "qualitative_level_not_found";

export type SegmentModelErrorCode =
  | "segment_count_too_small"
  | "segment_count_too_large"
  | "duplicate_segment_id"
  | "duplicate_segment_rank"
  | "empty_segment_name"
  | "segment_overlap"
  | "segment_gap"
  | "ambiguous_segment_boundary"
  | "missing_best_segment"
  | "multiple_best_segments"
  | "missing_worst_segment"
  | "multiple_worst_segments"
  | "invalid_best_worst_rank"
  | "score_outside_segment_range"
  | "segment_not_found";

export type CustomerValueScaleErrorCode = NumericScaleErrorCode | QualitativeScaleErrorCode | SegmentModelErrorCode;

export interface CustomerValueScaleValidationError {
  code: CustomerValueScaleErrorCode;
  path: string;
  messageKey: string;
  metadata?: Record<string, string | number | boolean | null>;
}

export interface NumericScaleInterval {
  id: string;
  scoreValue: number;
  minValue: number | null;
  maxValue: number | null;
  minInclusive: boolean;
  maxInclusive: boolean;
  order: number;
}

export interface NumericScaleDefinition {
  criterionId: string;
  unit: CustomerValueScaleUnit;
  direction: CustomerValueScaleDirection;
  pointsMin: number;
  pointsMax: number;
  intervals: readonly NumericScaleInterval[];
  completenessMode: NumericScaleCompletenessMode;
}

export interface QualitativeScaleLevel {
  id: string;
  label: string;
  description?: string;
  scoreValue: number;
  order: number;
  active: boolean;
}

export interface QualitativeScaleDefinition {
  criterionId: string;
  pointsMin: number;
  pointsMax: number;
  levels: readonly QualitativeScaleLevel[];
  allowDuplicateActiveScores?: boolean;
}

export interface CustomerValueSegmentDefinition {
  id: string;
  name: string;
  description?: string;
  rank: number;
  minScore: number;
  maxScore: number;
  minInclusive: boolean;
  maxInclusive: boolean;
  isBest: boolean;
  isWorst: boolean;
  active: boolean;
}

export interface SegmentModelDefinition {
  minimumScore?: number;
  maximumScore?: number;
  maxSegments?: number;
  segments: readonly CustomerValueSegmentDefinition[];
}

export interface ValidationResult<T> {
  ok: boolean;
  errors: CustomerValueScaleValidationError[];
  value?: T;
}

export interface NumericScoreResolution {
  interval: NumericScaleInterval;
  scoreValue: number;
}

export interface QualitativeScoreResolution {
  level: QualitativeScaleLevel;
  scoreValue: number;
}

export interface SegmentResolution {
  segment: CustomerValueSegmentDefinition;
}

export const DEFAULT_SEGMENT_SCORE_RANGE = { minimumScore: 0, maximumScore: 100 } as const;
export const MIN_SEGMENT_COUNT = 2;
export const DEFAULT_MAX_SEGMENTS = 12;
export const MAX_SEGMENT_COUNT = DEFAULT_MAX_SEGMENTS;

export const UNIT_PRECISION: Record<CustomerValueScaleUnit, number> = {
  currency: 2,
  percent: 4,
  absolute: 6,
  hours: 4,
  count: 0,
  ratio: 6,
  custom: 6
};

const EPSILON = 1e-9;

function error(
  code: CustomerValueScaleErrorCode,
  path: string,
  metadata?: CustomerValueScaleValidationError["metadata"]
): CustomerValueScaleValidationError {
  return { code, path, messageKey: `customerValue.scales.${code}`, metadata };
}

function precisionFactor(precision: number): number {
  return 10 ** precision;
}

export function normalizeValueForUnit(value: number, unit: CustomerValueScaleUnit): number {
  const factor = precisionFactor(UNIT_PRECISION[unit]);
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function validateSegmentCount(segmentCount: number): ValidationResult<number> {
  if (!Number.isInteger(segmentCount) || segmentCount < MIN_SEGMENT_COUNT) {
    return {
      ok: false,
      errors: [error("segment_count_too_small", "segmentCount", { count: segmentCount, minimum: MIN_SEGMENT_COUNT })]
    };
  }
  if (segmentCount > MAX_SEGMENT_COUNT) {
    return {
      ok: false,
      errors: [error("segment_count_too_large", "segmentCount", { count: segmentCount, maximum: MAX_SEGMENT_COUNT })]
    };
  }
  return { ok: true, errors: [], value: segmentCount };
}

function compareNumbers(left: number, right: number, unit: CustomerValueScaleUnit = "custom"): number {
  const normalizedLeft = normalizeValueForUnit(left, unit);
  const normalizedRight = normalizeValueForUnit(right, unit);
  if (Math.abs(normalizedLeft - normalizedRight) <= EPSILON) return 0;
  return normalizedLeft < normalizedRight ? -1 : 1;
}

function hasDuplicate<T>(items: readonly T[], getKey: (item: T) => string | number): Array<string | number> {
  const seen = new Set<string | number>();
  const duplicates = new Set<string | number>();
  for (const item of items) {
    const key = getKey(item);
    if (seen.has(key)) duplicates.add(key);
    seen.add(key);
  }
  return [...duplicates];
}

export function normalizeNumericIntervals(scale: NumericScaleDefinition): NumericScaleInterval[] {
  return [...scale.intervals].sort((left, right) => {
    if (left.minValue === null && right.minValue !== null) return -1;
    if (left.minValue !== null && right.minValue === null) return 1;
    if (left.minValue !== null && right.minValue !== null) {
      const minComparison = compareNumbers(left.minValue, right.minValue, scale.unit);
      if (minComparison !== 0) return minComparison;
    }
    if (left.maxValue === null && right.maxValue !== null) return 1;
    if (left.maxValue !== null && right.maxValue === null) return -1;
    if (left.maxValue !== null && right.maxValue !== null) {
      const maxComparison = compareNumbers(left.maxValue, right.maxValue, scale.unit);
      if (maxComparison !== 0) return maxComparison;
    }
    return left.order - right.order;
  });
}

function intervalContainsValue(interval: NumericScaleInterval, rawValue: number, unit: CustomerValueScaleUnit): boolean {
  const value = normalizeValueForUnit(rawValue, unit);
  if (interval.minValue !== null) {
    const minComparison = compareNumbers(value, interval.minValue, unit);
    if (minComparison < 0 || (minComparison === 0 && !interval.minInclusive)) return false;
  }
  if (interval.maxValue !== null) {
    const maxComparison = compareNumbers(value, interval.maxValue, unit);
    if (maxComparison > 0 || (maxComparison === 0 && !interval.maxInclusive)) return false;
  }
  return true;
}

function rangeErrorsForAdjacentIntervals(
  previous: NumericScaleInterval,
  current: NumericScaleInterval,
  unit: CustomerValueScaleUnit,
  overlapCode: NumericScaleErrorCode,
  gapCode: NumericScaleErrorCode,
  ambiguousCode: NumericScaleErrorCode,
  path: string
): CustomerValueScaleValidationError[] {
  if (previous.maxValue === null) {
    return [error("open_bound_not_outermost", `${path}.${previous.id}.maxValue`, { intervalId: previous.id })];
  }
  if (current.minValue === null) {
    return [error("open_bound_not_outermost", `${path}.${current.id}.minValue`, { intervalId: current.id })];
  }
  const boundaryComparison = compareNumbers(current.minValue, previous.maxValue, unit);
  if (boundaryComparison < 0) {
    return [error(overlapCode, path, { previousIntervalId: previous.id, currentIntervalId: current.id })];
  }
  if (boundaryComparison > 0) {
    return [error(gapCode, path, { previousIntervalId: previous.id, currentIntervalId: current.id })];
  }
  if (previous.maxInclusive && current.minInclusive) {
    return [error(ambiguousCode, path, { previousIntervalId: previous.id, currentIntervalId: current.id })];
  }
  if (!previous.maxInclusive && !current.minInclusive) {
    return [error(gapCode, path, { previousIntervalId: previous.id, currentIntervalId: current.id })];
  }
  return [];
}

export function detectNumericScaleOverlaps(scale: NumericScaleDefinition): CustomerValueScaleValidationError[] {
  const sorted = normalizeNumericIntervals(scale);
  const errors: CustomerValueScaleValidationError[] = [];
  for (let index = 1; index < sorted.length; index += 1) {
    errors.push(
      ...rangeErrorsForAdjacentIntervals(
        sorted[index - 1],
        sorted[index],
        scale.unit,
        "interval_overlap",
        "interval_gap",
        "ambiguous_boundary",
        "intervals"
      ).filter((entry) => entry.code === "interval_overlap" || entry.code === "ambiguous_boundary")
    );
  }
  return errors;
}

export function detectNumericScaleGaps(scale: NumericScaleDefinition): CustomerValueScaleValidationError[] {
  const sorted = normalizeNumericIntervals(scale);
  const errors: CustomerValueScaleValidationError[] = [];
  for (let index = 1; index < sorted.length; index += 1) {
    errors.push(
      ...rangeErrorsForAdjacentIntervals(
        sorted[index - 1],
        sorted[index],
        scale.unit,
        "interval_overlap",
        "interval_gap",
        "ambiguous_boundary",
        "intervals"
      ).filter((entry) => entry.code === "interval_gap")
    );
  }
  if (scale.completenessMode === "full_coverage") {
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    if (first && first.minValue !== null) errors.push(error("interval_gap", "intervals.0.minValue", { expected: "open_lower_bound" }));
    if (last && last.maxValue !== null) errors.push(error("interval_gap", `intervals.${last.id}.maxValue`, { expected: "open_upper_bound" }));
  }
  return errors;
}

function validateNumericIntervalShape(scale: NumericScaleDefinition): CustomerValueScaleValidationError[] {
  const errors: CustomerValueScaleValidationError[] = [];
  if (scale.intervals.length === 0) return [error("scale_empty", "intervals")];

  for (const duplicateId of hasDuplicate(scale.intervals, (interval) => interval.id)) {
    errors.push(error("duplicate_interval_id", "intervals", { id: String(duplicateId) }));
  }

  scale.intervals.forEach((interval, index) => {
    if (interval.scoreValue < scale.pointsMin || interval.scoreValue > scale.pointsMax) {
      errors.push(error("score_out_of_range", `intervals.${index}.scoreValue`, { scoreValue: interval.scoreValue }));
    }
    if (
      interval.minValue !== null &&
      interval.maxValue !== null &&
      compareNumbers(interval.minValue, interval.maxValue, scale.unit) >= 0
    ) {
      errors.push(error("invalid_interval_bounds", `intervals.${index}`, { intervalId: interval.id }));
    }
    if (interval.minValue === null && interval.maxValue === null && scale.intervals.length > 1) {
      errors.push(error("invalid_interval_bounds", `intervals.${index}`, { intervalId: interval.id }));
    }
  });

  const sorted = normalizeNumericIntervals(scale);
  sorted.forEach((interval, index) => {
    if (interval.minValue === null && index !== 0) {
      errors.push(error("open_bound_not_outermost", `intervals.${interval.id}.minValue`, { intervalId: interval.id }));
    }
    if (interval.maxValue === null && index !== sorted.length - 1) {
      errors.push(error("open_bound_not_outermost", `intervals.${interval.id}.maxValue`, { intervalId: interval.id }));
    }
  });

  for (let index = 1; index < sorted.length; index += 1) {
    errors.push(
      ...rangeErrorsForAdjacentIntervals(
        sorted[index - 1],
        sorted[index],
        scale.unit,
        "interval_overlap",
        "interval_gap",
        "ambiguous_boundary",
        "intervals"
      )
    );
  }

  if (scale.completenessMode === "full_coverage") {
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    if (first?.minValue !== null) errors.push(error("interval_gap", "intervals.0.minValue", { expected: "open_lower_bound" }));
    if (last?.maxValue !== null) errors.push(error("interval_gap", `intervals.${last?.id ?? "last"}.maxValue`, { expected: "open_upper_bound" }));
  }

  if (scale.direction !== "target_range_better") {
    for (let index = 1; index < sorted.length; index += 1) {
      const previous = sorted[index - 1];
      const current = sorted[index];
      const scoreComparison = compareNumbers(current.scoreValue, previous.scoreValue);
      if (scale.direction === "higher_better" && scoreComparison < 0) {
        errors.push(error("non_monotonic_score", `intervals.${current.id}.scoreValue`, { previousIntervalId: previous.id, currentIntervalId: current.id }));
      }
      if (scale.direction === "lower_better" && scoreComparison > 0) {
        errors.push(error("non_monotonic_score", `intervals.${current.id}.scoreValue`, { previousIntervalId: previous.id, currentIntervalId: current.id }));
      }
    }
  }

  return errors;
}

export function validateNumericScale(scale: NumericScaleDefinition): ValidationResult<NumericScaleInterval[]> {
  const errors = validateNumericIntervalShape(scale);
  return { ok: errors.length === 0, errors, value: errors.length === 0 ? normalizeNumericIntervals(scale) : undefined };
}

export function resolveNumericScore(scale: NumericScaleDefinition, rawValue: number): ValidationResult<NumericScoreResolution> {
  const validation = validateNumericScale(scale);
  if (!validation.ok) return { ok: false, errors: validation.errors };

  const matches = validation.value?.filter((interval) => intervalContainsValue(interval, rawValue, scale.unit)) ?? [];
  if (matches.length === 0) {
    return { ok: false, errors: [error("raw_value_unmatched", "rawValue", { rawValue: normalizeValueForUnit(rawValue, scale.unit) })] };
  }
  if (matches.length > 1) {
    return { ok: false, errors: [error("raw_value_ambiguous", "rawValue", { rawValue: normalizeValueForUnit(rawValue, scale.unit) })] };
  }
  return { ok: true, errors: [], value: { interval: matches[0], scoreValue: matches[0].scoreValue } };
}

export function validateQualitativeScale(scale: QualitativeScaleDefinition): ValidationResult<QualitativeScaleLevel[]> {
  const activeLevels = scale.levels.filter((level) => level.active);
  const errors: CustomerValueScaleValidationError[] = [];
  if (activeLevels.length < 2) errors.push(error("qualitative_scale_too_small", "levels"));

  for (const duplicateId of hasDuplicate(scale.levels, (level) => level.id)) {
    errors.push(error("duplicate_level_id", "levels", { id: String(duplicateId) }));
  }
  for (const duplicateOrder of hasDuplicate(activeLevels, (level) => level.order)) {
    errors.push(error("duplicate_level_order", "levels", { order: Number(duplicateOrder) }));
  }
  if (!scale.allowDuplicateActiveScores) {
    for (const duplicateScore of hasDuplicate(activeLevels, (level) => level.scoreValue)) {
      errors.push(error("qualitative_score_out_of_range", "levels", { duplicateScore: Number(duplicateScore), reason: "duplicate_active_score" }));
    }
  }

  scale.levels.forEach((level, index) => {
    if (!level.label.trim()) errors.push(error("empty_level_label", `levels.${index}.label`, { id: level.id }));
    if (level.scoreValue < scale.pointsMin || level.scoreValue > scale.pointsMax) {
      errors.push(error("qualitative_score_out_of_range", `levels.${index}.scoreValue`, { id: level.id, scoreValue: level.scoreValue }));
    }
  });

  const normalized = [...activeLevels].sort((left, right) => left.order - right.order);
  return { ok: errors.length === 0, errors, value: errors.length === 0 ? normalized : undefined };
}

export function resolveQualitativeScore(scale: QualitativeScaleDefinition, levelId: string): ValidationResult<QualitativeScoreResolution> {
  const validation = validateQualitativeScale(scale);
  if (!validation.ok) return { ok: false, errors: validation.errors };
  const level = validation.value?.find((entry) => entry.id === levelId);
  if (!level) return { ok: false, errors: [error("qualitative_level_not_found", "levelId", { levelId })] };
  return { ok: true, errors: [], value: { level, scoreValue: level.scoreValue } };
}

export function normalizeSegments(model: SegmentModelDefinition): CustomerValueSegmentDefinition[] {
  return [...model.segments]
    .filter((segment) => segment.active)
    .sort((left, right) => {
      const minComparison = compareNumbers(left.minScore, right.minScore);
      if (minComparison !== 0) return minComparison;
      return left.rank - right.rank;
    });
}

function segmentContainsScore(segment: CustomerValueSegmentDefinition, score: number): boolean {
  const minComparison = compareNumbers(score, segment.minScore);
  if (minComparison < 0 || (minComparison === 0 && !segment.minInclusive)) return false;
  const maxComparison = compareNumbers(score, segment.maxScore);
  if (maxComparison > 0 || (maxComparison === 0 && !segment.maxInclusive)) return false;
  return true;
}

function rangeErrorsForAdjacentSegments(
  previous: CustomerValueSegmentDefinition,
  current: CustomerValueSegmentDefinition
): CustomerValueScaleValidationError[] {
  const preservesLegacyIntegerBoundary =
    previous.maxInclusive &&
    current.minInclusive &&
    Number.isInteger(previous.maxScore) &&
    Number.isInteger(current.minScore) &&
    compareNumbers(current.minScore, previous.maxScore + 1) === 0;
  if (preservesLegacyIntegerBoundary) return [];

  const boundaryComparison = compareNumbers(current.minScore, previous.maxScore);
  if (boundaryComparison < 0) {
    return [error("segment_overlap", "segments", { previousSegmentId: previous.id, currentSegmentId: current.id })];
  }
  if (boundaryComparison > 0) {
    return [error("segment_gap", "segments", { previousSegmentId: previous.id, currentSegmentId: current.id })];
  }
  if (previous.maxInclusive && current.minInclusive) {
    return [error("ambiguous_segment_boundary", "segments", { previousSegmentId: previous.id, currentSegmentId: current.id })];
  }
  if (!previous.maxInclusive && !current.minInclusive) {
    return [error("segment_gap", "segments", { previousSegmentId: previous.id, currentSegmentId: current.id })];
  }
  return [];
}

export function detectSegmentOverlaps(model: SegmentModelDefinition): CustomerValueScaleValidationError[] {
  const sorted = normalizeSegments(model);
  const errors: CustomerValueScaleValidationError[] = [];
  for (let index = 1; index < sorted.length; index += 1) {
    errors.push(...rangeErrorsForAdjacentSegments(sorted[index - 1], sorted[index]).filter((entry) =>
      entry.code === "segment_overlap" || entry.code === "ambiguous_segment_boundary"
    ));
  }
  return errors;
}

export function detectSegmentGaps(model: SegmentModelDefinition): CustomerValueScaleValidationError[] {
  const sorted = normalizeSegments(model);
  const errors: CustomerValueScaleValidationError[] = [];
  for (let index = 1; index < sorted.length; index += 1) {
    errors.push(...rangeErrorsForAdjacentSegments(sorted[index - 1], sorted[index]).filter((entry) => entry.code === "segment_gap"));
  }
  const minimumScore = model.minimumScore ?? DEFAULT_SEGMENT_SCORE_RANGE.minimumScore;
  const maximumScore = model.maximumScore ?? DEFAULT_SEGMENT_SCORE_RANGE.maximumScore;
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (first && compareNumbers(first.minScore, minimumScore) !== 0) errors.push(error("segment_gap", "segments.minimumScore", { minimumScore }));
  if (last && compareNumbers(last.maxScore, maximumScore) !== 0) errors.push(error("segment_gap", "segments.maximumScore", { maximumScore }));
  if (first && compareNumbers(first.minScore, minimumScore) === 0 && !first.minInclusive) {
    errors.push(error("segment_gap", "segments.minimumScore", { minimumScore, reason: "exclusive_minimum_boundary" }));
  }
  if (last && compareNumbers(last.maxScore, maximumScore) === 0 && !last.maxInclusive) {
    errors.push(error("segment_gap", "segments.maximumScore", { maximumScore, reason: "exclusive_maximum_boundary" }));
  }
  return errors;
}

export function validateSegmentModel(model: SegmentModelDefinition): ValidationResult<CustomerValueSegmentDefinition[]> {
  const activeSegments = normalizeSegments(model);
  const minimumScore = model.minimumScore ?? DEFAULT_SEGMENT_SCORE_RANGE.minimumScore;
  const maximumScore = model.maximumScore ?? DEFAULT_SEGMENT_SCORE_RANGE.maximumScore;
  const maxSegments = model.maxSegments ?? DEFAULT_MAX_SEGMENTS;
  const errors: CustomerValueScaleValidationError[] = [];

  if (activeSegments.length < 2) errors.push(error("segment_count_too_small", "segments", { count: activeSegments.length }));
  if (activeSegments.length > maxSegments) errors.push(error("segment_count_too_large", "segments", { count: activeSegments.length, maxSegments }));

  for (const duplicateId of hasDuplicate(model.segments, (segment) => segment.id)) {
    errors.push(error("duplicate_segment_id", "segments", { id: String(duplicateId) }));
  }
  for (const duplicateRank of hasDuplicate(activeSegments, (segment) => segment.rank)) {
    errors.push(error("duplicate_segment_rank", "segments", { rank: Number(duplicateRank) }));
  }

  activeSegments.forEach((segment, index) => {
    if (!segment.name.trim()) errors.push(error("empty_segment_name", `segments.${index}.name`, { id: segment.id }));
    if (compareNumbers(segment.minScore, segment.maxScore) >= 0) {
      errors.push(error("segment_gap", `segments.${index}`, { id: segment.id, reason: "invalid_bounds" }));
    }
    if (compareNumbers(segment.minScore, minimumScore) < 0 || compareNumbers(segment.maxScore, maximumScore) > 0) {
      errors.push(error("score_outside_segment_range", `segments.${index}`, { id: segment.id, minimumScore, maximumScore }));
    }
  });

  for (let index = 1; index < activeSegments.length; index += 1) {
    errors.push(...rangeErrorsForAdjacentSegments(activeSegments[index - 1], activeSegments[index]));
  }
  errors.push(...detectSegmentGaps(model).filter((entry) => !errors.some((existing) => existing.code === entry.code && existing.path === entry.path)));

  const bestSegments = activeSegments.filter((segment) => segment.isBest);
  const worstSegments = activeSegments.filter((segment) => segment.isWorst);
  if (bestSegments.length === 0) errors.push(error("missing_best_segment", "segments"));
  if (bestSegments.length > 1) errors.push(error("multiple_best_segments", "segments"));
  if (worstSegments.length === 0) errors.push(error("missing_worst_segment", "segments"));
  if (worstSegments.length > 1) errors.push(error("multiple_worst_segments", "segments"));

  const ranks = activeSegments.map((segment) => segment.rank);
  const minRank = Math.min(...ranks);
  const maxRank = Math.max(...ranks);
  const best = bestSegments[0];
  const worst = worstSegments[0];
  if (best && best.rank !== minRank) errors.push(error("invalid_best_worst_rank", "segments.best", { id: best.id, rank: best.rank, expectedRank: minRank }));
  if (worst && worst.rank !== maxRank) errors.push(error("invalid_best_worst_rank", "segments.worst", { id: worst.id, rank: worst.rank, expectedRank: maxRank }));

  return { ok: errors.length === 0, errors, value: errors.length === 0 ? activeSegments : undefined };
}

export function resolveSegmentForScore(model: SegmentModelDefinition, score: number): ValidationResult<SegmentResolution> {
  const minimumScore = model.minimumScore ?? DEFAULT_SEGMENT_SCORE_RANGE.minimumScore;
  const maximumScore = model.maximumScore ?? DEFAULT_SEGMENT_SCORE_RANGE.maximumScore;
  if (compareNumbers(score, minimumScore) < 0 || compareNumbers(score, maximumScore) > 0) {
    return { ok: false, errors: [error("score_outside_segment_range", "score", { score, minimumScore, maximumScore })] };
  }

  const validation = validateSegmentModel(model);
  if (!validation.ok) return { ok: false, errors: validation.errors };
  const matches = validation.value?.filter((segment) => segmentContainsScore(segment, score)) ?? [];
  if (matches.length === 0) return { ok: false, errors: [error("segment_not_found", "score", { score })] };
  if (matches.length > 1) return { ok: false, errors: [error("ambiguous_segment_boundary", "score", { score })] };
  return { ok: true, errors: [], value: { segment: matches[0] } };
}
